import { trackInteraction } from 'client/javascript/analytics';
import { isAvailable } from 'client/javascript/availability';
import { logError } from 'client/javascript/util/logger';
import { showElements, hideElements } from 'client/javascript/util/dom_utils';
import { ISODateToLocalizedShortDate } from 'client/javascript/util/locale';
import * as storage from 'client/javascript/storage';
import initFlatpickr from 'client/javascript/filters/date_picker';
import { __ } from 'client/javascript/messages';
import User, { USER_STORAGE_KEY_MAP } from 'shared/storage/user';
import { formatIsoDate, formatDateForPicker, newDateFromIsoString } from 'shared/util/date';
import { MAX_CAPACITY_FOR_TOUR } from 'client/javascript/util/constants';
import createFocusTrap from 'focus-trap';
import { makeLeadServiceRequest } from '../../default_leads_form_data';
import { setupPhoneInput } from '../../phone';
import { varsFromServer } from '../../client_side_injection_vars';
import { submitLead, getTimes, getNextToursDates } from '../../submit_lead';
import { sendLeadFormHeapEvents } from '../../form_analytics';
import Form from '../../lead_form';
import NumberStepper from '../../number_stepper';

const ONE_DAY_IN_MILLIS = 24 * 3600 * 1000;

const LEAD_SOURCE_BOOK_TOUR_FORM = 'Book A Tour Form';
const LEAD_SOURCE_WAIT_LIST_FORM = 'Waiting List Form';
const LEAD_SOURCE_AVAILABILITY_FORM = 'Book A Tour Availability';
const LEAD_SOURCE_RESERVE = 'Reserve Workspace';

// In-Person/Virtual Tours
const LSD_VIRTUAL_TOUR_STEP_1 = 'Book a Virtual Tour Availability';
const LSD_VIRTUAL_TOUR_STEP_2 = 'Book a Virtual Tour Form';
const IN_PERSON_TOUR_TYPE = 'In-Person';
const VIRTUAL_TOUR_TYPE = 'Virtual';

const EXPERIENCE_SIZE_OVER_MAX = `size over ${MAX_CAPACITY_FOR_TOUR}`;
const EXPERIENCE_BUILDING_AVAILABLE = 'available';
const EXPERIENCE_BUILDING_UNAVAILABLE = 'unavailable';

export const OPEN_FORM_BTN_INTERACTION_LABEL = 'Open Book a Tour form';
export const CLOSE_FORM_BTN_INTERACTION_LABEL = 'Close Book a Tour form';

const RESERVE_LEAD_FORM_STEP_ONE = 'reserve-form-step-1';

export default class BuildingForm {
  constructor() {
    // Lead forms
    this.leadFormElements = document.querySelectorAll('form.lead-form-step-1');
    this.tourFormElement = document.getElementById('tour-form-step-2');
    this.tourFormElements = document.querySelectorAll('.tour-form-step-2');

    // Lead form elements
    this.tourTypeToggle = document.getElementById('js-tour-type-toggle');
    this.leadFormBackBtn = document.getElementById('lead-form-back-btn');
    this.leadFormFooters = document.querySelectorAll('.lead-form-contact-footer');
    this.moveInDateWrapper = document.getElementById('moveInDateWrapper');
    this.leadFormStepOneHeaders = document.querySelectorAll('.lead-form-header');
    this.tourTimesSelectEl = document.getElementById('tour-times');
    this.tourTimesSelectElements = document.querySelectorAll('.tour-times__select');
    this.step2SubmitEl = document.getElementById('step2Submit');
    this.tourTimesSelectEl = document.getElementById('tour-times');
    this.leadFormStepOneHeader = document.querySelector('.lead-form-header');
    this.moveInDateWrapper = document.getElementById('moveInDateWrapper');
    this.mobileHeadline = document.querySelector('.form-headline .lead-form-building-name');
    this.closeButtons = document.querySelectorAll('.close-form');

    // Lead form messages
    this.availabilityCheckFailElement = document.getElementById('availibility-check-fail');
    this.buildingUnavailableElement = document.getElementById('building-unavailable');
    this.sizeOverMaxElement = document.getElementById('building-size-over-max');
    this.submitSuccess = document.querySelectorAll('#submit-success');
    this.reserveSubmitSuccess = document.querySelectorAll('#reserve-success');
    this.inPersonTourCopy = document.querySelectorAll('.js-in-person-tour-form-messages');
    this.virtualTourCopy = document.querySelectorAll('.js-virtual-tour-form-messages');

    this.initMoveInDate();

    // Currently selected tour format
    this.tourFormat = null;
  }

  initMoveInDate() {
    if (!this.moveInDateWrapper) return;
    this.moveInDateBtn = document.getElementById('moveInDateBtn');
    this.moveInDateInput = document.getElementById('moveInDate');
    const savedDate = User.getValue(USER_STORAGE_KEY_MAP.startDateFilter);
    if (savedDate) {
      this.moveInDateInput.value = savedDate;
      this.moveInDateBtn.textContent = ISODateToLocalizedShortDate(savedDate);
    }

    initFlatpickr({
      input: this.moveInDateInput,
      onChange: this.handleDateChange,
      appendTo: this.moveInDateWrapper
    }).then(datePicker => {
      this.datePicker = datePicker;

      this.moveInDateBtn.addEventListener('click', this.datePicker.toggle);

      this.moveInDateClear = document.getElementById('search-clear__date');
      this.moveInDateClear.addEventListener('click', this.handleDateClearClick);
      showElements(this.moveInDateClear);

      // simulate ray select component
      this.moveInDateBtn.addEventListener('focus', () => this.moveInDateWrapper.classList.add('ray-select--active'));
      this.moveInDateBtn.addEventListener('blur', () => this.moveInDateWrapper.classList.remove('ray-select--active'));
    });
  }

  handleDateChange = dates => {
    let formattedDate;
    if (dates.length) {
      formattedDate = ISODateToLocalizedShortDate(dates[0], false);
      User.saveValue(USER_STORAGE_KEY_MAP.startDateFilter, this.moveInDateInput.value);
    } else {
      formattedDate = __('moveInDateNotSelected');
      User.clearValue(USER_STORAGE_KEY_MAP.startDateFilter);
    }

    this.moveInDateBtn.textContent = formattedDate;
  };

  handleDateClearClick = () => {
    this.datePicker.clear();
    this.datePicker.close();
  };

  toggleTourTypeCopy(selectedTourType) {
    if (selectedTourType === IN_PERSON_TOUR_TYPE) {
      hideElements(...this.virtualTourCopy);
      showElements(...this.inPersonTourCopy);
    } else if (selectedTourType === VIRTUAL_TOUR_TYPE) {
      hideElements(...this.inPersonTourCopy);
      showElements(...this.virtualTourCopy);
    }
  }

  handleTourTypeToggle = event => {
    this.toggleTourTypeCopy(event.target.value);
  };

  setupTourTypeToggle() {
    if (this.tourTypeToggle) {
      const selectedTourType = this.tourTypeToggle.querySelector('input:checked');
      if (!selectedTourType) {
        return;
      }
      this.tourTypeToggle
        .querySelectorAll('input')
        .forEach(element => element.addEventListener('click', this.handleTourTypeToggle));
      this.toggleTourTypeCopy(selectedTourType.value);
    }
  }

  setupFirstLeadForm() {
    this.setupTourTypeToggle();
    if (this.leadFormBackBtn) this.leadFormBackBtn.addEventListener('click', this.onClickBackButton);

    if (this.closeButtons) {
      this.closeButtons.forEach(button => button.addEventListener('click', this.onClickCloseButton));
    }

    this.leadFormElements.forEach(
      element => element && setupPhoneInput(element.querySelector('#phone'), { appendToBodyInMobile: false })
    );

    this.leadFormElements.forEach(element => element && new Form(element, this.handleStepOneOnSubmit));

    const numStepperElement = document.getElementById('capacity-number-stepper');
    // eslint-disable-next-line no-new
    new NumberStepper(numStepperElement, {
      onChange() {
        /* this forces validation on change since change/blur events don't fire when user interacts with the increment/decrement buttons. */
        this.leadFormElements.forEach(element => element.setInputValidity(this.input));
      }
    });
  }

  addNoteToTourForm(note) {
    const noteEl = this.tourFormElement.querySelector('textarea[name="notes"]');
    if (noteEl) {
      noteEl.innerText = note;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  setUpLeadFormButtonMobile() {
    const leadFormOpenButtons = document.querySelectorAll('#open-form-modal');
    const leadFormCloseButtons = document.querySelectorAll('.close-form-modal');
    let leadFormFocusTrap;
    const { classList } = document.body;

    if (leadFormOpenButtons) {
      leadFormOpenButtons.forEach(button => {
        button.addEventListener('click', e => {
          classList.add('mobile-form-open');
          classList.add('js-lock-scroll');
          leadFormFocusTrap = createFocusTrap(`.${e.target.dataset.type} #lead-form-container`);
          leadFormFocusTrap.activate();
          trackInteraction({
            label: OPEN_FORM_BTN_INTERACTION_LABEL
          });
        });
      });

      leadFormCloseButtons.forEach(button => {
        button.addEventListener('click', () => {
          classList.remove('mobile-form-open');
          classList.remove('js-lock-scroll');
          this.showStepOne();
          leadFormFocusTrap.deactivate();
          trackInteraction({
            label: CLOSE_FORM_BTN_INTERACTION_LABEL
          });
        });
      });
    }
  }

  onClickBackButton = () => {
    this.showStepOne();
    trackInteraction({
      label: 'BAT Form Back Button'
    });
  };

  onClickCloseButton = () => {
    this.showStepOne();
    trackInteraction({
      label: CLOSE_FORM_BTN_INTERACTION_LABEL
    });
  };

  showStepOne() {
    hideElements(...this.tourFormElements, ...this.reserveSubmitSuccess, ...this.submitSuccess, this.leadFormBackBtn);
    showElements(...this.leadFormElements, ...this.leadFormFooters, ...this.leadFormStepOneHeaders);
  }

  // eslint-disable-next-line class-methods-use-this
  async warmTourDatePicker(date, numberOfDaysToWarm) {
    for (let i = 0; i < numberOfDaysToWarm; i++) {
      date.setDate(date.getDate() + 1);
      const dateStr = formatIsoDate(date);
      // eslint-disable-next-line no-await-in-loop
      const availableTimes = await getTimes(varsFromServer.building, dateStr);
      const noAvailability = !availableTimes || availableTimes.length === 0;
      if (noAvailability) {
        global.__disabledDates__.push(dateStr);
        const daySelector = `#tour-date-wrapper .flatpickr-day[aria-label='${formatDateForPicker(date)}']`;
        const dayEl = document.querySelector(daySelector);
        if (dayEl) dayEl.classList.add('disabled');
      }
    }
  }

  async disableUnavailableTourDates() {
    try {
      global.__disabledDates__ = [];
      global.__nextTours__ = await getNextToursDates(varsFromServer.building);
    } catch (error) {
      global.__nextTours__ = [];
    }

    const lastDateStr = global.__nextTours__[global.__nextTours__.length - 1];

    if (lastDateStr) {
      const lastDate = newDateFromIsoString(lastDateStr);
      this.warmTourDatePicker(lastDate, 14);
    }
  }

  async showStepTwo() {
    if (!this.initedTourForm) {
      await this.disableUnavailableTourDates();

      // Set datapicker
      await Promise.all(Array.from(this.tourFormElements).map(tourForm => this.initializeLeadFormDatePicker(tourForm)));

      this.tourFormElements.forEach(tourForm => {
        this.setOptionPlaceholderText(tourForm);
        // eslint-disable-next-line no-new
        new Form(tourForm, this.handleStepTwoSubmit);
      });

      this.initedTourForm = true;
    }

    hideElements(...this.leadFormElements, ...this.leadFormFooters, this.leadFormStepOneHeader);
    showElements(...this.tourFormElements, this.leadFormBackBtn);
  }

  showCustomizationMessage() {
    hideElements(
      this.buildingUnavailableElement,
      ...this.leadFormElements,
      ...this.leadFormFooters,
      this.leadFormStepOneHeader
    );
    showElements(this.sizeOverMaxElement);
  }

  showUnavailableMessage() {
    hideElements(
      this.sizeOverMaxElement,
      ...this.leadFormElements,
      ...this.leadFormFooters,
      this.leadFormStepOneHeader
    );
    showElements(this.buildingUnavailableElement);
  }

  showAllSet() {
    if (this.leadFormBackBtn) this.leadFormBackBtn.classList.add('disabled');
    hideElements(
      ...this.leadFormElements,
      ...this.tourFormElements,
      ...this.leadFormStepOneHeaders,
      ...this.leadFormFooters,
      this.leadFormBackBtn
    );
    showElements(...this.reserveSubmitSuccess);
  }

  showThankYou() {
    hideElements(
      ...this.leadFormStepOneHeaders,
      ...this.leadFormElements,
      ...this.tourFormElements,
      ...this.leadFormFooters,
      this.leadFormBackBtn,
      this.mobileHeadline
    );
    if (this.leadFormBackBtn) this.leadFormBackBtn.classList.add('disabled');
    showElements(...this.submitSuccess);
  }

  showAvailabilityCheckErrorMessage() {
    hideElements(...this.leadFormElements, ...this.leadFormFooters, this.leadFormStepOneHeader);
    showElements(this.availabilityCheckFailElement);
  }

  renderStepTwoMessage(formValues, isDeskAvailable) {
    const isNotOpenButTourable = !varsFromServer.building.is_open && varsFromServer.building.is_tourable;

    // Do we still need to figure out if the picked date is after next month?
    // For now, keep old logic
    let isMoveInDateOther = !formValues.moveInDate;
    if (formValues.moveInDate) {
      const date = new Date(formValues.moveInDate);
      const now = new Date();
      const monthDiff = (date.getYear() - now.getYear()) * 12 + date.getMonth() - now.getMonth();
      if (monthDiff > 1) {
        isMoveInDateOther = true;
      }
    }

    if (isNotOpenButTourable || isMoveInDateOther || isDeskAvailable) {
      this.updateCapacityText(formValues.desiredCapacity);
      this.showStepTwo();
      this.stepOneValues.experience = EXPERIENCE_BUILDING_AVAILABLE;
    } else {
      this.showUnavailableMessage();
      this.stepOneValues.experience = EXPERIENCE_BUILDING_UNAVAILABLE;
    }
  }

  getStepOneLeadSourceDetail({ isReserve, building }) {
    let leadSourceDetail = LEAD_SOURCE_AVAILABILITY_FORM;

    if (this.tourFormat && this.tourFormat === VIRTUAL_TOUR_TYPE) {
      leadSourceDetail = LSD_VIRTUAL_TOUR_STEP_1;
    }

    if (building.has_waitlist) {
      leadSourceDetail = LEAD_SOURCE_WAIT_LIST_FORM;
    }

    if (isReserve) {
      leadSourceDetail = LEAD_SOURCE_RESERVE;
    }

    return leadSourceDetail;
  }

  getStepTwoLeadSourceDetail() {
    switch (this.tourFormat) {
      case IN_PERSON_TOUR_TYPE:
        return LEAD_SOURCE_BOOK_TOUR_FORM;
      case VIRTUAL_TOUR_TYPE:
        return LSD_VIRTUAL_TOUR_STEP_2;
      default:
        return LEAD_SOURCE_BOOK_TOUR_FORM;
    }
  }

  handleStepOneOnSubmit = async ({ target }, formValues) => {
    this.hideSubmissionError();
    let isDeskAvailable;
    const isReserve = target.dataset.reserve === 'true';
    this.tourFormat = formValues.tour_format;
    const shouldHidePricing = target.dataset.shouldHidePricing === 'true';

    let availabilityTimeoutError = false;
    const { building, reservableDetails } = varsFromServer;

    const leadSource = this.getStepOneLeadSourceDetail({ isReserve, building });

    const { desiredCapacity } = formValues;

    try {
      isDeskAvailable = await isAvailable(formValues, building);
    } catch (error) {
      logError('Availability API call timeout error', error);
      availabilityTimeoutError = true;
    }

    const leadFormData = { ...formValues, isDeskAvailable };

    // We don't need to send tour_format for step one as it is a lead submission
    delete leadFormData.tour_format;

    leadFormData.building_uuid = varsFromServer.building.id;

    if (isReserve) {
      const { discount, commitment } = target.dataset;
      building.reserve = isReserve;
      leadFormData.reservableData = { ...reservableDetails, discount, commitment };
    }

    // to be used in lead source subtype
    building.shouldHidePricing = shouldHidePricing;
    this.stepOneValues = leadFormData;
    this.addVirtualTourNotes(leadFormData);

    try {
      const leadServiceRequest = makeLeadServiceRequest(leadFormData, building, 1);
      await submitLead(leadServiceRequest, leadSource);

      const leadFormAnalyticsData = {
        ...leadFormData,
        lead_source_subtype: leadServiceRequest.lead_source_subtype
      };
      this.trackSubmission(leadFormAnalyticsData, leadSource);
      if (building.is_whitelist && target.id === RESERVE_LEAD_FORM_STEP_ONE) {
        this.showAllSet();
      } else if (!building.is_tourable || shouldHidePricing) {
        this.showThankYou();
      } else if (this.isGreaterThanMaxCapacity(desiredCapacity)) {
        this.showCustomizationMessage();
        this.stepOneValues.experience = EXPERIENCE_SIZE_OVER_MAX;
      } else if (availabilityTimeoutError) {
        this.showAvailabilityCheckErrorMessage();
      } else {
        this.renderStepTwoMessage(formValues, isDeskAvailable);
      }

      trackInteraction({
        label: 'Building Page Tour Form Routing',
        company_size: desiredCapacity,
        move_in_date: formValues.moveInDate,
        experience: this.stepOneValues.experience
      });
    } catch (error) {
      logError('Lead form step one submit error', error);
      this.showSubmissionError();
    }
  };

  handleStepTwoSubmit = async (evt, values) => {
    const stepTwoValues = values;
    const leadSource = this.getStepTwoLeadSourceDetail(this.stepOneValues.tour_format);

    this.hideSubmissionError();

    const leadFormData = {
      ...this.stepOneValues,
      ...stepTwoValues,
      ...(this.tourFormat && { tour_format: this.tourFormat })
    };
    this.addVirtualTourNotes(leadFormData);

    try {
      const leadServiceRequest = makeLeadServiceRequest(leadFormData, varsFromServer.building, 2);
      await submitLead(leadServiceRequest, leadSource);

      const leadFormAnalyticsData = {
        ...leadFormData,
        lead_source_subtype: leadServiceRequest.lead_source_subtype
      };
      this.trackSubmission(leadFormAnalyticsData, leadSource);
      this.showThankYou();
    } catch (error) {
      logError('Lead form step two submit error', error);
      this.showSubmissionError();
    }
  };

  // eslint-disable-next-line class-methods-use-this
  isGreaterThanMaxCapacity(desiredCapacity) {
    const capacity = Number.parseInt(desiredCapacity, 10);
    return capacity > MAX_CAPACITY_FOR_TOUR;
  }

  updateCapacityText(desiredCapacity) {
    if (!this.tourFormElement) {
      return;
    }

    const capacityTexts = Array.from(this.tourFormElement.querySelectorAll('.desired-capacity'));

    if (capacityTexts.length) {
      // only present if the building is open
      capacityTexts.forEach(el => {
        el.textContent = desiredCapacity; // eslint-disable-line no-param-reassign
      });
    }
  }

  async initializeLeadFormDatePicker(form) {
    const { init: initDatePicker } = await import(/* webpackChunkName: "datepicker" */ './date_picker');

    const tourDateEl = form.querySelector('#tour-date');

    await initDatePicker({
      element: tourDateEl,
      appendTo: form.querySelector('#tour-date-wrapper')
    });

    tourDateEl.addEventListener('change', () => {
      this.handleDatePickerChange(tourDateEl.value);
    });
  }

  disableTourTimes(isDisabled) {
    this.tourTimesSelectElements.forEach(tourTime => {
      tourTime.disabled = isDisabled; // eslint-disable-line no-param-reassign
    });
  }

  async handleDatePickerChange(dateStr) {
    this.disableTourTimes(true);

    const availableTimes = await getTimes(varsFromServer.building, dateStr);

    if (!availableTimes || availableTimes.length === 0) {
      return this.handleNoAvailableTimes(dateStr);
    }

    this.disableTourTimes(false);
    this.handleAvailableTimes(availableTimes);
  }

  handleNoAvailableTimes(dateStr) {
    global.__disabledDates__.push(dateStr);
    const date = newDateFromIsoString(dateStr);
    this.warmTourDatePicker(date, 14);

    this.step2SubmitEl.disabled = true;

    this.tourTimesSelectElements.forEach(tourTimesSelectElement => {
      tourTimesSelectElement.parentElement.classList.add('select-disabled');
      tourTimesSelectElement.innerHTML = '<option value="" disabled selected>No times available</option>'; // eslint-disable-line no-param-reassign
    });
  }

  handleAvailableTimes(availableTimes) {
    this.step2SubmitEl.disabled = false;

    this.tourTimesSelectElements.forEach(tourTimesSelectElement => {
      tourTimesSelectElement.parentElement.classList.remove('select-disabled');
      const options = availableTimes.map(date => `<option value="${date.time}">${date.time}</option>`);
      options.unshift(`<option value="" disabled selected>${this.optionPlaceholderText}</option>`);
      tourTimesSelectElement.innerHTML = options.join(''); // eslint-disable-line no-param-reassign
    });
  }

  setOptionPlaceholderText(form) {
    // Kind of terrible but we need to grab the placeholder text ("Select a Time")
    // because we re-render it on the client and it might be translated, so can't hard-code
    const option = form.querySelector('#tour-times option');
    if (this.optionPlaceholderText) {
      return;
    }
    if (option) {
      this.optionPlaceholderText = form.querySelector('#tour-times option').innerText;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  hideSubmissionError() {
    hideElements('.submission-exception-message');
  }

  // eslint-disable-next-line class-methods-use-this
  showSubmissionError() {
    showElements('.submission-exception-message');
  }

  // eslint-disable-next-line class-methods-use-this
  trackSubmission(leadFormData, leadSource) {
    const data = {
      ...leadFormData,
      hasVirtualTour: varsFromServer.building && varsFromServer.building.has_virtual_tour,
      tourType: this.tourFormat
    };
    sendLeadFormHeapEvents(data, leadSource, leadFormData.lead_source_subtype);
  }

  addVirtualTourNotes(leadFormData) {
    // user saw the virtual tour today
    if (this.justVisitedVirtualTour()) {
      const virtualTourTaken = 'Virtual-Tour-WeWork.com';
      if (leadFormData.notes) {
        if (!leadFormData.notes.includes(virtualTourTaken)) {
          leadFormData.notes = `${leadFormData.notes};\n${virtualTourTaken}`; // eslint-disable-line no-param-reassign
        }
      } else {
        leadFormData.notes = virtualTourTaken; // eslint-disable-line no-param-reassign
      }
    }
  }

  // eslint-disable-next-line class-methods-use-this
  justVisitedVirtualTour() {
    const { slug } = varsFromServer.building;
    const virtualTourTimeStamp = storage.getVirtualTour(slug);
    if (virtualTourTimeStamp) {
      const ts = Number(virtualTourTimeStamp);
      return Date.now() - ts < ONE_DAY_IN_MILLIS;
    }
  }
}
